<template>
    <div
        class="cb-block"
        :style="{
            top: `${metas[block.id].y}px`,
            left: `${metas[block.id].x}px`,
            ...BlockStyles[block.type],
        }"
        :class="{
            active: active || $_.get(movingMode ? movingBlock : copyingBlock, 'id') === block.id,
            'child-move-disabled':
                (movingMode || copyingMode) && isChildBlock(movingMode ? movingBlock.id : copyingBlock.id, block.id),
            'moving-mode': movingMode || copyingMode,
            'preview-mode': previewMode,
            'drag-mode': dragMode,
            'drag-hover': dragHover,
            'drop-disallowed': dropWarningVisible,
            'has-child': !!block.children.length,
            'no-child': !block.children.length,
            terminated: [
                BLOCK_TYPES.CLOSE_CHAT,
                BLOCK_TYPES.TRANSFER_CHAT,
                BLOCK_TYPES.TRANSFER_TO_CHATBOT,
                BLOCK_TYPES.AI_ASSISTANT,
            ].includes(block.type),
        }"
        :data-type="block.type"
        @dragenter="dragOver"
        @dragleave="dragLeave"
        @dragover="allowDrop"
        @drop="dropBlock"
        @mouseenter="mouseEnter"
        @mouseleave="mouseLeave"
    >
        <div :class="{ 'selected-block': selected }" style="width: 100%; height: 100%" @mouseleave="more = false">
            <div class="name" v-if="!isAIBlock()">
                <span class="-editable-block-tittle" v-if="!editName">{{ getBlockDisplayName() }}</span>
                <input
                    :id="'name_input-' + block.id"
                    v-else
                    type="text"
                    v-model="name"
                    @blur="editName = false"
                    @keydown.enter="editName = false"
                />
            </div>
            <a-dropdown-button
                @click="editName = true"
                class="block-menu"
                v-if="!previewMode && !editName && !movingMode && !copyingMode && !isAIBlock()"
                :trigger="['click']"
                placement="topRight"
            >
                {{ $t("chatbotv2.editName") }}
                <a-menu slot="overlay" @click="$_.noop" placement="topRight">
                    <a-menu-item key="collapse" @click="collapse" :disabled="!block.children.length">
                        <a-icon type="shrink" style="font-size: 15px" />
                        {{ collapsed ? $t("chatbotv2.expand") : $t("chatbotv2.collapse") }}
                    </a-menu-item>
                    <a-menu-item key="collapseAll" @click="collapseAll" :disabled="!block.children.length">
                        <a-icon type="shrink" style="font-size: 15px" />
                        {{ collapsedAll ? $t("chatbotv2.expandAll") : $t("chatbotv2.collapseAll") }}
                    </a-menu-item>
                    <a-menu-divider />
                    <a-menu-item
                        :disabled="
                            ['START_POINT', 'DEFAULT_FALLBACK', 'FLOW', 'SUCCESS', 'FAILURE'].includes(block.type)
                        "
                        key="editBlock"
                        @click="(e) => $emit('focusBlock', e, block)"
                    >
                        <a-icon type="edit" style="font-size: 15px" />
                        {{ $t("chatbotv2.editBlock") }}
                    </a-menu-item>
                    <a-menu-item v-if="isShowingMoveBlock()" key="moveBlock" @click="changeMoveMode(block)">
                        <a-icon type="drag" style="font-size: 15px" />
                        {{ $t("chatbotv2.moveBlock") }}
                    </a-menu-item>
                    <a-menu-item v-if="isShowingCopyBlock()" key="copyBlock" @click="changeCopyMode(block)">
                        <a-icon type="copy" style="font-size: 15px" />
                        {{ $t("chatbotv2.copyBlock") }}
                    </a-menu-item>
                    <a-menu-divider />
                    <a-menu-item
                        v-if="isShowingDeleteSingleBlock()"
                        key="deleteSingleBlock"
                        @click="showDeleteConfirmation('single')"
                    >
                        <a-icon type="delete" style="font-size: 15px" class="text-danger" />
                        {{ $t("chatbotv2.deleteSingleBlock") }}
                    </a-menu-item>
                    <a-menu-item
                        v-if="isShowingDeleteBlockWithChildren()"
                        key="deleteWithChildren"
                        @click="showDeleteConfirmation('multi')"
                    >
                        <a-icon type="delete" style="font-size: 15px" class="text-danger" />
                        {{ $t("chatbotv2.deleteWithChildren") }}
                    </a-menu-item>
                    <a-menu-divider />
                    <a-menu-item key="startTestFromHere" @click="startTestFromHere(block)">
                        <a-icon type="play-circle" style="font-size: 15px" />
                        {{ $t("chatbotv2.startTestFromHere") }}
                    </a-menu-item>
                    <a-menu-divider />
                    <a-menu-item key="copyBlockId" @click="copyBlockId(block)">
                        <a-icon type="copy" style="font-size: 15px" />
                        {{ $t("chatbotv2.copyBlockId") }}
                    </a-menu-item>
                    <a-menu-item key="gotoLiveLinkFromBlock" @click="gotoLiveLinkFromBlock(block)">
                        <a-icon type="share-alt" style="font-size: 15px" />
                        {{ $t("chatbotv2.gotoLiveLink") }}
                    </a-menu-item>
                    <!-- new feature: copy block as json to clipboard to transfer to other chatbots -->
                    <a-menu-item key="copyBlockAsJson" @click="copyBlockAsJson(block)">
                        <a-icon type="copy" style="font-size: 15px" />
                        {{ $t("chatbotv2.copyBlockAsJson") }}
                    </a-menu-item>
                    <a-menu-item key="pasteBlockFromClipboard" @click="pasteBlockFromClipboard">
                        <a-icon type="copy" style="font-size: 15px" />
                        {{ $t("chatbotv2.pasteBlockFromClipboard") }}
                    </a-menu-item>
                </a-menu>
            </a-dropdown-button>
            <div
                v-if="
                    (collapsed && block.type !== 'FLOW') ||
                    dragMode ||
                    movingMode ||
                    copyingMode ||
                    needShowAddContextMenu()
                "
                class="addContext"
                :class="{
                    'd-block': collapsed,
                    dragMode,
                    movingMode:
                        (movingMode || copyingMode) &&
                        !(
                            block.children.length > 0 ||
                            (!metas[block.id].isRoot && block.type === 'FLOW') ||
                            isChildBlock(movingMode ? movingBlock.id : copyingBlock.id, block.id) ||
                            [BLOCK_TYPES.TRANSFER_CHAT, BLOCK_TYPES.TRANSFER_TO_CHATBOT].includes(block.type)
                        ),
                }"
                @click="
                    (e) =>
                        movingMode || copyingMode
                            ? movingMode
                                ? moveBlock(block.id)
                                : copyBlock(block.id)
                            : collapsed
                            ? collapse()
                            : $emit('openContextMenu', { e, block })
                "
            >
                <md-icon>{{ collapsed ? "expand" : "add" }}</md-icon>
            </div>
            <!--            <b-spinner v-if="testPosition === block.id && !testClosed" class="pulseBlock" type="grow" />-->
            <div v-if="testPosition === block.id && !testClosed" class="pulseBlock">
                <div></div>
                <div></div>
                <div></div>
            </div>
            <div @click="handleBlockClick($event)" class="block-content">
                <div
                    class="d-flex align-items-center align-content-center justify-content-start block-icon"
                    @click="openAIPage()"
                >
                    <BlockIconPreview :block-type="block.type" :custom-block-icon="getCustomBlockIcon()" />
                    <span class="blockTypeText" v-if="block.type === BLOCK_TYPES.AKINON_ORDER_STATUS"
                        >{{ $t("chatbotv2.orderStatus") }}</span
                    >
                    <span class="blockTypeText" v-if="block.type === BLOCK_TYPES.CUSTOM_BLOCK">
                        {{ getBlockDisplayName() }}
                    </span>
                    <span class="blockTypeText" v-else>{{ $t(`chatbotv2.${block.type}`) }} </span>
                    <span
                        class="md-icon"
                        v-if="block.type === BLOCK_TYPES.GO_TO_STEP && (!hasValidTargetBlockId || !targetBlockExists)"
                        style="color: red; margin-left: 20px; animation: blink 1s infinite;"
                    >
                        warning
                    </span>
                </div>

                <div
                    class="move-warning"
                    v-if="
                        movingMode &&
                        block.children.length > 0 &&
                        block.type !== 'FLOW' &&
                        !isChildBlock(movingBlock.id, block.id)
                    "
                    v-tippy="{
                        followCursor: true,
                        placement: 'bottom-end',
                    }"
                    :content="$t('chatbotv2.blockCannotBeChild', { draggingType: movingBlock.type, blockType: block.type })"
                >
                    <a-icon type="warning" theme="filled" style="color: #b4c0c7; font-size: 24px" />
                </div>
                <div
                    class="move-warning"
                    v-if="
                        copyingMode &&
                        block.children.length > 0 &&
                        block.type !== 'FLOW' &&
                        !isChildBlock(copyingBlock.id, block.id)
                    "
                    v-tippy="{
                        followCursor: true,
                        placement: 'bottom-end',
                    }"
                    :content="$t('chatbotv2.blockCannotBeChild', { draggingType: copyingBlock.type, blockType: block.type })"
                >
                    <a-icon type="warning" theme="filled" style="color: #b4c0c7; font-size: 24px" />
                </div>
                <a-alert
                    :message="$t('chatbotv2.blockCannotBeChild', { draggingType: draggingType, blockType: block.type })"
                    type="warning"
                    class="drop-warning-alert"
                    v-if="dropWarningVisible"
                />
                <div class="drop-warning" v-if="dropWarningVisible">
                    <a-icon type="warning" theme="filled" style="color: #b4c0c7; font-size: 24px" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { log2 } from "../../../../../../utils/log2";
import { ActionPalette, BlockStyles } from "../../../chatbot";
import { mapActions, mapMutations, mapState } from "vuex";
import BLOCK_TYPES from "../../../block.types";
import BlockIconPreview from "../common/BlockIconPreview";
import { copyText2Clipboard } from "../../../../../../utils/utils";
import * as _ from "lodash";
import CONFIG from "../../../../../../config/config";

const log = log2("Block");
export default {
    name: "Block",
    components: {
        BlockIconPreview,
    },
    props: [
        "block",
        "metas",
        "active",
        "board",
        "storyId",
        "storyVersion",
        "movingMode",
        "movingBlock",
        "moveBlock",
        "changeMoveMode",
        "copyingMode",
        "copyingBlock",
        "copyBlock",
        "changeCopyMode",
        "flattedDiagram",
        "showNotification",
        "dragMode",
        "addBlockToDiagram",
        "draggingType",
        "collapsed",
        "collapsedAll",
        "startTestFromHere",
        "testPosition",
        "testClosed",
        "selected",
        "previewMode",
        "getAllowedBlocks",
        "showAIKnowledge",
    ],
    data() {
        return {
            BlockStyles,
            ActionPalette,
            editName: false,
            more: false,
            name: this.block.name,
            dragHover: false,
            dragHoverCounter: 0,
            BLOCK_TYPES,
        };
    },
    computed: {
        ...mapState({
            site: (state) => state.site.site,
            channels: (state) =>
                state.channel.channels.filter((x) => x.type === "openai" || x.type === "openrouter"),
            importedBlocks: (state) => state.customBlocks.importedBlocks,
        }),
        dropWarningVisible: function () {
            if (this.dragMode && this.dragHover) {
                //ortadaki ve sondaki flow'lara ekleme yapılamaz
                if (BLOCK_TYPES.FLOW === this.block.type && !this.metas[this.block.id].isRoot) {
                    return true;
                }
                //eklenecek tip çok cocuklu olduğundan sadece çoçuksuz olanlara eklenebilir
                if (
                    [BLOCK_TYPES.QUESTION, BLOCK_TYPES.QUESTION].includes(this.draggingType) &&
                    this.block.children.length > 0
                ) {
                    return true;
                }
                //user input'lar sadece bot response dan sonra girilir
                if (this.draggingType === BLOCK_TYPES.USER_INPUT && this.block.type !== BLOCK_TYPES.BOT_RESPONSE) {
                    return true;
                }
                //start point'te default  fallback dışında 1 tane child varsa eklenemez
                if (
                    this.block.type === BLOCK_TYPES.START_POINT &&
                    !this.block.children.filter((x) => x.type !== BLOCK_TYPES.DEFAULT_FALLBACK).length >= 1
                ) {
                    return true;
                }

                const terminatingBlocks = [
                    BLOCK_TYPES.CLOSE_CHAT,
                    BLOCK_TYPES.GO_TO_STEP,
                    BLOCK_TYPES.TRANSFER_CHAT,
                    BLOCK_TYPES.TRANSFER_TO_CHATBOT,
                ];
                //ezici son bloklardan sonra birşey eklenemez
                if (terminatingBlocks.includes(this.block.type)) {
                    return true;
                }
                //artı ile eklenemeyen sürüklenerek de eklenemez.
                if (!this.getAllowedBlocks(this.block).includes(this.draggingType)) {
                    return true;
                }
            }
            return false;
        },
        hasValidTargetBlockId() {
            const payload = this.block && this.block.payload ? this.block.payload : null;
            const blockId = payload && payload.blockId ? payload.blockId : "";
            const trimmed = typeof blockId === "string" && typeof blockId.trim === "function" ? blockId.trim() : blockId;
            return !!trimmed;
        },
        targetBlockExists() {
            if (!this.hasValidTargetBlockId) return false;
            return this.checkBlockExistsInDiagram(this.board.diagram, this.block.payload.blockId);
        },
    },
    methods: {
        getBlockDisplayName() {
            // For custom blocks, show the imported block name
            if (this.block.type === BLOCK_TYPES.CUSTOM_BLOCK && this.block.payload?.customBlockId) {
                const importedBlock = this.importedBlocks.find(
                    (ib) => ib.blockId === this.block.payload.customBlockId
                );
                if (importedBlock) {
                    return importedBlock.customName || this.$_.get(importedBlock, 'block.name', this.block.name);
                }
            }
            return this.block.name;
        },
        getCustomBlockIcon() {
            // For custom blocks, get the icon from imported block
            if (this.block.type === BLOCK_TYPES.CUSTOM_BLOCK && this.block.payload?.customBlockId) {
                const importedBlock = this.importedBlocks.find(
                    (ib) => ib.blockId === this.block.payload.customBlockId
                );
                if (importedBlock) {
                    return this.$_.get(importedBlock, 'block.icon', 'extension');
                }
            }
            return null;
        },
        handleBlockClick(e) {
            if (this.isAIBlock()) return;
            if (
                this.block.type === BLOCK_TYPES.GO_TO_STEP &&
                e && e.metaKey &&
                this.hasValidTargetBlockId &&
                this.targetBlockExists
            ) {
                this.$emit("gotoTarget", this.block.payload.blockId);
                return;
            }
            this.$emit("focusBlock", e, this.block);
        },
        ...mapActions({
            updateBlock: "CB/updateBlock",
            deleteSingleBlock: "CB/deleteSingleBlock",
            deleteWithChildrenD: "CB/deleteWithChildren",
            updateBoardSingleKey: "CB/updateBoardSingleKey",
            insertMultipleBlocks: "CB/insertMultipleBlocks",
        }),
        ...mapMutations({
            collapseToggle: "CB/COLLAPSE_TOGGLE",
        }),
        copyBlockId(block) {
            if (block) {
                copyText2Clipboard(block.id);
                this.$message.success(this.$t("chatbotv2.copyBlockIdSuccessfull"));
            }
        },
        async pasteBlockFromClipboard() {
            const isValidPartialBlock = await this.isPartialBlockExistsInClipboard();
            if (isValidPartialBlock) {
                const text = await navigator.clipboard.readText();
                const json = JSON.parse(text);
                console.log(`pasting block`, {
                    id: this.storyId,
                    version: this.storyVersion,
                    targetBlockId: this.block.id,
                    block: json.data,
                    text,
                    json,
                });
                this.insertMultipleBlocks({
                    id: this.storyId,
                    version: this.storyVersion,
                    targetBlockId: this.block.id,
                    block: json.data,
                });
            } else {
                this.$message.error("Invalid partial block. Please copy a valid partial block.");
            }
        },
        async isPartialBlockExistsInClipboard() {
            try {
                const clipboard = navigator.clipboard;
                const text = await clipboard.readText();
                return text.includes("chatbot-partial-block");
            } catch (err) {
                return false;
            }
        },
        copyBlockAsJson(block) {
            if (block) {
                const json = {
                    type: "chatbot-partial-block",
                    data: block,
                };
                copyText2Clipboard(JSON.stringify(json));
                this.$message.success("Block copied to clipboard");
            }
        },
        gotoLiveLinkFromBlock(block) {
            if (block) {
                this.$message.success(this.$t("chatbotv2.copyBlockIdSuccessfull"));
                const customerClientAddress = _.get(this.site, "customerClientAddress");
                if (customerClientAddress && customerClientAddress !== "undefined") {
                    const protocol = CONFIG.PRODUCTION ? "https" : "http";
                    const link = `${protocol}://${customerClientAddress}?chatbot_start_block_id=${block.id}`;
                    window.open(link, "_blank");
                }
            }
        },
        isShowingDeleteSingleBlock() {
            const pathname = `${window.location.pathname}`;
            if (
                ["START_POINT", "DEFAULT_FALLBACK"].includes(this.block.type) ||
                this.block.children.length > 1 ||
                (pathname.includes("/flow/") && this.block.type === "FLOW") ||
                (["TIMEOUT"].includes(this.block.type) && this.block.children.length > 0) ||
                (this.board.diagram.children[0].id === this.block.id &&
                    this.block.type !== BLOCK_TYPES.DEFAULT_TIMEOUT) ||
                (this.board.diagram.children[0].id === this.block.id && this.block.type !== BLOCK_TYPES.AI_FUNCTIONS)
            )
                return false;
            else return true;
        },
        isShowingDeleteBlockWithChildren() {
            const pathname = `${window.location.pathname}`;
            if (
                ["START_POINT", "DEFAULT_FALLBACK"].includes(this.block.type) ||
                this.block.children.length === 0 ||
                (pathname.includes("/flow/") && this.block.type === "FLOW")
            )
                return false;
            else return true;
        },
        isShowingMoveBlock() {
            const pathname = `${window.location.pathname}`;
            if (
                ["START_POINT", "DEFAULT_FALLBACK", "DEFAULT_TIMEOUT", "AI_FUNCTIONS"].includes(this.block.type) ||
                (pathname.includes("/flow/") && this.block.type === "FLOW")
            )
                return false;
            else return true;
        },
        isShowingCopyBlock() {
            const pathname = `${window.location.pathname}`;
            if (
                ["START_POINT", "DEFAULT_FALLBACK", "DEFAULT_TIMEOUT", "AI_FUNCTIONS"].includes(this.block.type) ||
                (pathname.includes("/flow/") && this.block.type === "FLOW")
            )
                return false;
            else return true;
        },

        mouseEnter() {
            if (
                this.block.type === BLOCK_TYPES.GO_TO_STEP &&
                this.hasValidTargetBlockId &&
                this.targetBlockExists
            ) {
                this.$emit("gotoHover", { fromId: this.block.id, toId: this.block.payload.blockId });
            }
        },
        mouseLeave() {
            if (this.block.type === BLOCK_TYPES.GO_TO_STEP) {
                this.$emit("gotoHoverOut");
            }
        },
        dragOver(e) {
            if (!this.dragMode) return;
            this.dragHoverCounter++;
            // log("dragOver");
            if (!this.dragHover) {
                this.dragHover = true;
            }
        },
        dragLeave() {
            // log("dragLeave");
            this.dragHoverCounter--;
            if (this.dragHover && this.dragHoverCounter === 0) {
                this.dragHover = false;
            }
        },
        dropBlock(ev) {
            const type = ev.dataTransfer.getData("text");
            log("dropBlock", type, ev);
            if (type) {
                if (this.dropWarningVisible) {
                    this.showNotification(`The block ${type} can't be child of  the ${this.block.type} block`, "error");
                } else {
                    this.addBlockToDiagram(this.block.id, type);
                }
            }
            this.dragHoverCounter = 0;
            this.dragHover = false;
        },
        allowDrop(ev) {
            // log("allowDrop", ev);
            if (this.isAIBlock()) return false;
            ev.preventDefault();
        },
        openAIPage() {
            if (this.block.type === BLOCK_TYPES.AI_ASSISTANT) {
                if (this.channels.length > 0) {
                    this.showAIKnowledge();
                } else {
                    this.$message.error("Please add OpenAI or OpenRouter channel first");
                }
            }
        },
        isAIBlock() {
            return this.block.type === BLOCK_TYPES.AI_ASSISTANT;
        },
        isChildBlock(parentId, childId) {
            const childBlock = this.flattedDiagram.find((x) => x.id === childId);

            if (!childBlock) return false;
            let startBlock = childBlock;
            let result = startBlock.id === parentId;
            if (result) return true;
            while (startBlock.parent) {
                result = startBlock.parent.id === parentId || startBlock.id === parentId;
                startBlock = startBlock.parent;
                if (result) break;
            }
            log("isChildBlock", { parentId, childId, childBlock, result });
            return result;
        },
        needShowAddContextMenu() {
            // console.log("needShowAddContextMenu", {
            //     previewMode: this.previewMode,
            //     block: this.block,
            //     isTerminationBlock: BLOCK_TYPES.TERMINATION_BLOCKS.includes(this.block.type),
            //     isEitherBlock: BLOCK_TYPES.MULTI_CHILD_BLOCKS.includes(this.block.type),
            //     isAIAssistantBlock: this.block.type === BLOCK_TYPES.AI_ASSISTANT,
            // });
            if (this.previewMode) return false;
            const meta = this.metas[this.block.id];
            if (this.block.type === "FLOW" && !meta.isRoot) return false;
            const isTerminationBlock = BLOCK_TYPES.TERMINATION_BLOCKS.includes(this.block.type);
            const isEitherBlock = BLOCK_TYPES.MULTI_CHILD_BLOCKS.includes(this.block.type);
            const isAIAssistantBlock = this.block.type === BLOCK_TYPES.AI_ASSISTANT;
            if (isTerminationBlock || isAIAssistantBlock) return false;
            else {
                if (isEitherBlock) {
                    return this.block.children.length < 2;
                } else return true;
            }
        },
        updateName() {
            if (this.name === this.block.name) return;
            this.updateBlock({
                id: this.storyId,
                version: this.storyVersion,
                blockId: this.block.id,
                payload: {
                    name: this.name,
                },
            });
        },
        showDeleteConfirmation(deleteType) {
            this.$confirm({
                title:
                    deleteType === "single"
                        ? this.$t("chatbotv2.douYouWannaDeleteItem")
                        : this.$t("chatbotv2.douYouWannaDeleteWithChildren"),
                onOk: () => {
                    deleteType === "single" ? this.deleteBlock() : this.deleteWithChildren();
                },
                onCancel() {},
            });
        },
        deleteBlock() {
            this.deleteSingleBlock({
                id: this.storyId,
                version: this.storyVersion,
                blockId: this.block.id,
            })
                .then((x) => {
                    this.showNotification(
                        `The block ${this.block.name || this.block.type} has been deleted`,
                        "success",
                        true
                    );
                })
                .catch((e) => {
                    this.showNotification(
                        `An error occured, the block ${this.block.name || this.block.type} has not been deleted`,
                        "error",
                        true
                    );
                });
        },
        deleteWithChildren() {
            this.deleteWithChildrenD({
                id: this.storyId,
                version: this.storyVersion,
                blockId: this.block.id,
            })
                .then((x) => {
                    this.showNotification(
                        `The block ${this.block.name || this.block.type} has been deleted with children`,
                        "success",
                        true
                    );
                })
                .catch((e) => {
                    this.showNotification(
                        `An error occurred, the block ${this.block.name || this.block.type} has not been deleted`,
                        "error",
                        true
                    );
                });
        },
        collapse() {
            this.updateBoardSingleKey({
                id: this.storyId,
                version: this.storyVersion,
                key: "state",
                value: {
                    ...this.board.state,
                    collapsed: this.board.state.collapsed.includes(this.block.id)
                        ? this.board.state.collapsed.filter((x) => x !== this.block.id)
                        : this.board.state.collapsed.concat([this.block.id]),
                },
            });
        },
        findAllChildsId(block) {
            let ids = [block.id];
            if (!!block.children && block.children.length !== 0) {
                block.children.forEach((child) => {
                    ids = ids.concat(this.findAllChildsId(child));
                });
            }
            return ids;
        },
        collapseAll() {
            let ids = this.findAllChildsId(this.block);
            ids.pop();
            this.updateBoardSingleKey({
                id: this.storyId,
                version: this.storyVersion,
                key: "state",
                value: {
                    ...this.board.state,
                    collapsed: this.board.state.collapsed.includes(ids[0])
                        ? this.board.state.collapsed.filter((x) => !ids.includes(x))
                        : this.board.state.collapsed.concat(ids),
                },
            });
        },
        checkBlockExistsInDiagram(diagram, targetId) {
            if (diagram.id === targetId) return true;
            if (diagram.children && diagram.children.length) {
                return diagram.children.some(function (child) {
                    return this.checkBlockExistsInDiagram(child, targetId);
                }, this);
            }
            return false;
        },
    },
    mounted() {
        // log(`Block Mounted [${this.block.type}] ${this.block.name}`, {
        //     testPosition: this.testPosition,
        //     blockId: this.block.id,
        //     testClosed: this.testClosed,
        // });
    },
    updated() {
        // log(`Block Updated [${this.block.type}] ${this.block.name}`, {
        //     testPosition: this.testPosition,
        //     blockId: this.block.id,
        //     testClosed: this.testClosed,
        // });
    },
    watch: {
        editName(nv, ov) {
            if (nv) setTimeout(() => document.getElementById("name_input-" + this.block.id).focus(), 300);
            if (!nv) this.updateName();
        },
    },
};
</script>

<style lang="scss">
@keyframes blink {
    0% {
        opacity: 1;
    }
    50% {
        opacity: 0;
    }
    100% {
        opacity: 1;
    }
}
@keyframes block-pulsar {
    0% {
        box-shadow: 0 0 0 0 rgba(0, 111, 255, 0.4);
    }
    70% {
        box-shadow: 0 0 0 45px rgba(0, 111, 255, 0);
    }
    100% {
        box-shadow: 0 0 0 0 rgba(204, 169, 44, 0);
    }
}

.selected-block {
    opacity: 0;
    background: #006cff;
    color: #ffffff;
    border-radius: 17px;
    animation: crystallize 2s;
    animation-fill-mode: forwards;
}

@keyframes crystallize {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

.cb-block {
    position: absolute;
    width: 190px;
    height: 40px;
    min-height: 40px;
    align-items: center;
    border-radius: 20px;
    box-shadow: 0 2px 15px #00000012;
    display: flex;
    transition: background-color 0.5s;
    user-select: none;
    cursor: pointer;

    .-editable-block-tittle {
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
        word-wrap: break-word;
        color: #516173;
        display: -webkit-box;
        font-size: 14px;
        font-weight: 600;
        left: -65px;
        line-height: 14px;
        max-height: 40px;
        overflow: hidden;
        position: absolute;
        right: -65px;
        text-align: center;
        text-overflow: ellipsis;
        -webkit-transition: color 0.5s;
        transition: color 0.5s;
    }

    .blockTypeText {
        margin-left: 5px;
        font-size: 12px;
        font-weight: 600;
        line-height: 20px;
        overflow: hidden !important;
        text-overflow: ellipsis;
        transition: color 0.5s;
        white-space: nowrap;
        color: #384350;
    }

    &[data-type="CLOSE_CHAT"],
    &[data-type="GO_TO_STEP"],
    &[data-type="ADD_TO_SEGMENT"],
    &[data-type="REMOVE_FROM_SEGMENT"],
    &[data-type="TRANSFER_CHAT"] {
        .addContext {
            display: none;
        }
    }

    &[data-type="CLOSE_CHAT"],
    &[data-type="TRANSFER_CHAT"],
    &[data-type="FALLBACK"],
    &[data-type="DEFAULT_FALLBACK"],
    &[data-type="SUCCESS"],
    &[data-type="FAILURE"] {
        .block-content {
            cursor: not-allowed;
        }
    }

    &[data-type="SUCCESS"],
    &[data-type="FAILURE"] {
        background: white !important;
    }

    &[data-type="USER_INPUT"],
    &[data-type="ATTACHMENT_INPUT"] {
        .blockTypeText {
            display: none;
        }
    }

    &[data-type="START_POINT"],
    &[data-type="AI_ASSISTANT"],
    &[data-type="FEEDBACK_AI_ASSISTANT"],
    &[data-type="INSERT_TO_SYSTEM_PROMPT"],
    &[data-type="FILTER"],
    &[data-type="SEND_TRANSCRIPT"],
    &[data-type="SEND_SMS"],
    &[data-type="SEND_MAIL"],
    &[data-type="CREATE_EVENT"],
    &[data-type="CREATE_RESERVATION"],
    &[data-type="RESERVATION_OPEN_URL"],
    &[data-type="RESERVATION_SELECT_DAY"],
    &[data-type="RESERVATION_SELECT_TIME"],
    &[data-type="RESERVATION_EVENT_LIST"],
    &[data-type="DEFAULT_FALLBACK"],
    &[data-type="SHOPIFY_SHOW_PRODUCTS"],
    &[data-type="SHOPIFY_PRODUCT_AVAILABILITY"],
    &[data-type="SHOPIFY_PRODUCT_AVAILABILITY"],
    &[data-type="SHOPIFY_ORDER_STATUS"],
    &[data-type="IDEASOFT_SELECT_ORDER"],
    &[data-type="IDEASOFT_ORDER_STATUS"],
    &[data-type="TSOFT_ORDER_STATUS"],
    &[data-type="TSOFT_SELECT_ORDER"],
    &[data-type="IDEASOFT_GET_MEMBER_INFO"],
    &[data-type="SET_ATTRIBUTE"],
    &[data-type="CREATE_TICKET"],
    &[data-type="WEBHOOK"],
    &[data-type="API_REQUEST"],
    &[data-type="CREATE_TASK"],
    &[data-type="SUPPRESSION_ADD"],
    &[data-type="ZOHO_CREATE_RECORD"],
    &[data-type="HOLIDAY_PLUS_GET_HOTELS"],
    &[data-type="HOLIDAY_PLUS_GET_ROOMS"],
    &[data-type="SUPPRESSION_REMOVE"],
    &[data-type="B2CARGO_LOGIN_USER"],
    &[data-type="B2CARGO_PRODUCT_STATUS"],
    &[data-type="FALLBACK"],
    &[data-type="DEFAULT_TIMEOUT"],
    &[data-type="AI_CALL"],
    &[data-type="AI_FUNCTIONS"],
    &[data-type="TIMEOUT"],
    &[data-type="TRANSFER_TO_CHATBOT"],
    &[data-type="TABLE_CREATE_RECORD"],
    &[data-type="TABLE_DELETE_RECORD"],
    &[data-type="TABLE_UPDATE_RECORD"],
    &[data-type="TABLE_FIND_RECORD"],
    &[data-type="TABLE_FIND_RECORDS"],
    &[data-type="INSTAGRAM_COMMENT_REPLY"],
    &[data-type="INSTAGRAM_COMMENT_DELETE"],
    &[data-type="INSTAGRAM_COMMENT_SHOW"],
    &[data-type="INSTAGRAM_COMMENT_HIDE"],
    &[data-type="INSTAGRAM_COMMENT_REPLY_AS_PRIVATE"],
    &[data-type="MESSENGER_SEND_MESSAGE"],
    &[data-type="INSTAGRAM_SEND_MESSAGE"],
    &[data-type="TICIMAX_V2_SELECT_ORDER"],
    &[data-type="TICIMAX_V2_ORDER_STATUS"],
    &[data-type="CUSTOM_BLOCK"],
    &[data-type="CACHE_WRITE"],
    &[data-type="CACHE_READ"],
    &[data-type="CACHE_EXTEND"],
    &[data-type="CACHE_DELETE"],
    &[data-type="SEND_WP_BUSINESS_MESSAGE"] {
        .blockTypeText {
            color: #fff;
        }
    }

    .block-content {
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        text-align: center;
        cursor: pointer;
        padding-left: 5px;

        .block-icon {
            .custom-block-logo {
                height: 24px;
                width: 24px;
                border-radius: 50%;
            }
        }
    }

    &[data-type="USER_INPUT"],
    &[data-type="ATTACHMENT_INPUT"] {
        background: transparent !important;
        display: flex;
        justify-content: center;
        height: 40px;
        max-width: none;
        min-width: auto;
        //padding: 10px;
        width: 40px;
        box-shadow: none !important;

        &:after {
            position: absolute;
            border: 2px solid #c9d3de;
            border-radius: 8px;
            content: "";
            flex-shrink: 0;
            height: 100%;
            transform: rotate(45deg);
            transition: background-color 0.5s, border 0.5s;
            width: 100%;
            top: 0 !important;
            z-index: -1;
            left: 0 !important;
        }

        .block-menu {
            transform: translateX(-170%);
        }
    }

    &:not(.moving-mode):hover,
    &:not(.drag):hover {
        & .nameContext,
        & .addContext,
        & .block-menu {
            display: block;
        }

        .move-warning {
            display: flex !important;
        }
    }

    .pulseBlock {
        height: 0;
        left: auto;
        position: absolute;
        right: -5px;
        top: 50%;
        width: 0;

        align-items: center;
        border-radius: 20px;
        color: #b4c0c7;
        cursor: pointer;
        display: flex;
        justify-content: center;

        div {
            @keyframes active-context-pulsar {
                0% {
                    height: 20px;
                    opacity: 0.3;
                    width: 20px;
                }
                to {
                    height: 50px;
                    width: 50px;
                }
            }

            &:nth-child(1) {
                background-image: radial-gradient(circle at 50% 50%, #b3d4ff, #89befb 76%);
                border-radius: 100%;
                content: "";
                height: 50px;
                opacity: 0.4;
                position: absolute;
                width: 50px;
            }

            &:nth-child(2) {
                animation: active-context-pulsar 1s infinite alternate;
                background-image: radial-gradient(circle at 50% 50%, #ddecfd, #006fff 76%);
                border-radius: 100%;
                content: "";
                height: 35px;
                opacity: 0.4;
                position: absolute;
                width: 35px;
            }

            &:nth-child(3) {
                background: #006cff;
                border-radius: 100%;
                height: 8px;
                position: absolute;
                width: 8px;
            }
        }
    }

    &.moving-mode.has-child:hover,
    &.moving-mode.terminated:hover {
        & .nameContext,
        & .addContext,
        & .block-menu {
            display: none !important;
        }

        .move-warning {
            display: flex !important;
        }
    }

    &:not(.drop-disallowed).drag-hover {
        border: 2px solid rgba(0, 111, 255, 0.4);
        color: white !important;
        background-color: rgba(0, 111, 255, 0.8) !important;
        box-shadow: 0 0 0 rgba(0, 111, 255, 0.4);
        animation: block-pulsar 2s infinite;

        & .nameContext,
        & .block-menu {
            display: none !important;
        }

        & .addContext {
            display: block !important;
        }
    }

    &.drop-disallowed.drag-hover {
        & .nameContext,
        & .block-menu,
        & .addContext {
            display: none !important;
        }
    }

    &:before {
        content: "";
        display: block;
        position: absolute;
        left: 0;
        top: -30px;
        right: -30px;
        bottom: 0;
        z-index: -1;
    }

    &.active {
        &:after {
            content: "";
            border: 2px dashed #00afff;
            position: absolute;
            left: -10px;
            right: -10px;
            top: -10px;
            bottom: -10px;
            border-radius: 30px;
        }
    }

    &.child-move-disabled {
        cursor: not-allowed;
        opacity: 0.3;
    }

    .name {
        position: absolute;
        top: -37px;
        left: 50%;
        transform: translateX(-50%);
        color: #000000;
        width: max-content;
        text-align: center;
        max-width: 180px;

        input {
            border: 1px solid #4c9dff;
            border-radius: 5px;
            padding: 4px;
            transform: translateY(-10px);
            background-color: transparent;
            text-align: center;
        }
    }

    .nameContext,
    .block-menu {
        display: none;
        position: absolute;
        bottom: 100%;
        left: 50%;
        transform: translateX(-50%);
        z-index: 999;
        width: 80%;
        padding-bottom: 0.5rem;
        color: #000000;

        .inner {
            background: #ffffff;
            padding: 0.5rem;
            border-radius: 5px;
            box-shadow: 0 2px 15px #00000012;
            display: flex;
            justify-content: space-between;
            align-items: center;

            .contextActions {
                position: absolute;
                right: 0;
                top: 100%;
                background: #ffffff;
                box-shadow: 0 2px 15px #00000033;
                border-radius: 4px;
                overflow: hidden;

                .chatbot-btn {
                    background: transparent;
                    display: block;
                    width: 100%;
                    border: none;
                    border-bottom: 1px solid #eeeeee;
                    padding: 0.5rem 1rem;
                    transition: all 0.3s ease-in-out;
                    text-align: left;

                    &:hover {
                        background: #6ca7ff;
                        color: #ffffff;
                    }

                    &:last-child {
                        border-bottom: none;
                    }
                }
            }
        }
    }

    .addContext {
        position: absolute;
        top: 50%;
        left: 110%;
        transform: translateY(-50%);
        background: #ffffff;
        border-radius: 50%;
        padding: 0.4rem;
        box-shadow: 0 2px 15px #00000012;
        display: none;
    }

    .addContext.movingMode {
        display: inherit !important;
        box-shadow: 0 0 0 rgba(0, 111, 255, 0.4);
        animation: block-pulsar 2s infinite;

        .md-icon {
            color: #1790ff !important;
        }
    }

    .move-warning,
    .drop-warning {
        background: rgba(34, 48, 74, 0.9);
        cursor: not-allowed;
        display: flex;
        justify-content: center;
        align-items: center;
        position: absolute;
        width: 100%;
        height: 100%;
        color: white;
        border-radius: 24px;
        display: none;
    }

    .drop-warning {
        display: flex !important;
    }

    .drop-warning-alert {
        position: absolute;
        width: 400px;
        top: -60px;
        background-color: #3d485b !important;
        border: 1px solid #3d485b !important;

        .ant-alert-message {
            color: #b4c0c7 !important;
        }
    }
}
</style>
